import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { Col, Row, Table } from 'antd';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import './styles.scss';
import { AxiosSingleton } from '../../utils/axiosinstance';
import SpinningCircle from '../spinningcircle';
import InsightsCard from '../insightscard';
import { availableCountries } from './selectorOptions';
import { AnghamiAdsFormSelector } from '../core/AnghamiAdsFormSelector';

import {
  AD_TYPE_LABELS,
  PLACEMENT_LABELS,
  SOURCE_LABELS,
  adTypeFilterOptions,
  placementFilterOptions,
  sortGroupRows,
  sourceFilterOptions,
  sumGroupMetrics,
  GroupRow,
  formatCount
} from '../../helpers/inventory-groups';
import type { StrDayRow, StrFilters, StrResponse } from './types';

const CAPACITY_COLOR = '#945cff';
const BOOKED_COLOR = '#D60031';
const STR_COLOR = '#1f9d55';

const formatPercent = (value: number) => `${value}%`;

const dailyColumns = [
  { title: 'Date', dataIndex: 'day', key: 'day' },
  {
    title: 'Capacity',
    dataIndex: 'total_impressions',
    key: 'total_impressions',
    align: 'right' as const,
    render: formatCount
  },
  {
    title: 'Booked',
    dataIndex: 'reserved_impressions',
    key: 'reserved_impressions',
    align: 'right' as const,
    render: formatCount
  },
  {
    title: 'In-House',
    dataIndex: 'house_impressions',
    key: 'house_impressions',
    align: 'right' as const,
    render: formatCount
  },
  { title: 'STR', dataIndex: 'str', key: 'str', align: 'right' as const, render: formatPercent }
];

const breakdownColumns = [
  {
    title: 'Source',
    dataIndex: 'source',
    key: 'source',
    render: (value: GroupRow['source']) => SOURCE_LABELS[value]
  },
  {
    title: 'Ad type',
    dataIndex: 'ad_type',
    key: 'ad_type',
    render: (value: GroupRow['ad_type']) => AD_TYPE_LABELS[value]
  },
  {
    title: 'Placement',
    dataIndex: 'placement',
    key: 'placement',
    render: (value: GroupRow['placement']) => PLACEMENT_LABELS[value]
  },
  ...dailyColumns.slice(1)
];

const metricsSummaryRow = (
  rows: StrDayRow[] | GroupRow[],
  label: string,
  leadingCells: number,
  className = 'str-table-total'
) => {
  const totals = sumGroupMetrics(rows);
  return (
    <Table.Summary.Row className={className}>
      <Table.Summary.Cell index={0} colSpan={leadingCells}>
        {label}
      </Table.Summary.Cell>
      <Table.Summary.Cell index={leadingCells} align="right">
        {formatCount(totals.total_impressions)}
      </Table.Summary.Cell>
      <Table.Summary.Cell index={leadingCells + 1} align="right">
        {formatCount(totals.reserved_impressions)}
      </Table.Summary.Cell>
      <Table.Summary.Cell index={leadingCells + 2} align="right">
        {formatCount(totals.house_impressions)}
      </Table.Summary.Cell>
      <Table.Summary.Cell index={leadingCells + 3} align="right">
        {formatPercent(totals.str)}
      </Table.Summary.Cell>
    </Table.Summary.Row>
  );
};

export default function SellThroughRate() {
  const axios = AxiosSingleton.axiosInstance;
  const [startDate, setStartDate] = useState(moment().subtract(7, 'days').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(moment().subtract(1, 'days').format('YYYY-MM-DD'));
  const [countries, setCountries] = useState<string[]>([]);
  const [filters, setFilters] = useState<StrFilters>({
    ad_source: 'all',
    ad_type: 'all',
    placement: 'all'
  });

  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [results, setResults] = useState<StrResponse | null>(null);
  // Filter changes can fire requests faster than they return. Only the most
  // recent request is allowed to update the page, so an older, slower response
  // never overwrites a newer one.
  const latestRequest = useRef(0);

  const getSTR = () => {
    latestRequest.current += 1;
    const requestId = latestRequest.current;
    setIsLoading(true);
    setErrorMessage('');

    const params = {
      start_date: startDate,
      end_date: endDate,
      countries,
      ...filters
    };

    axios
      .get<StrResponse>('/inventory/str', { params })
      .then((res) => {
        if (requestId !== latestRequest.current) return;
        setResults(res.data);
      })
      .catch((error) => {
        if (requestId !== latestRequest.current) return;
        setResults(null);
        setErrorMessage(
          error.response?.data?.message || 'An error occurred while calculating the STR.'
        );
      })
      .finally(() => {
        if (requestId !== latestRequest.current) return;
        setIsLoading(false);
      });
  };

  // The grouped rows are cached server-side per range and countries, so a
  // filter change is a cheap re-fetch. Only refetch once a result exists.
  useEffect(() => {
    if (results) {
      getSTR();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const chartData = results
    ? results.str_per_day.map((row) => ({
        ...row,
        available_impressions: row.total_impressions - row.reserved_impressions
      }))
    : [];

  const filtersDescription = [
    filters.ad_source === 'all' ? 'all sources' : SOURCE_LABELS[filters.ad_source],
    filters.ad_type === 'all' ? 'audio + video' : AD_TYPE_LABELS[filters.ad_type],
    filters.placement === 'all' ? 'all placements' : PLACEMENT_LABELS[filters.placement]
  ].join(', ');

  const hasActiveFilters =
    filters.ad_source !== 'all' || filters.ad_type !== 'all' || filters.placement !== 'all';

  const resetFilters = () => {
    setFilters({ ad_source: 'all', ad_type: 'all', placement: 'all' });
  };

  // The breakdown always lists every group so they can be compared. Rows that
  // match the filters are highlighted and summed into a "Selected groups" total
  // that equals the tiles above; the rest are dimmed.
  const allBreakdown = sortGroupRows(results?.breakdown);
  const matchesFilters = (row: GroupRow) =>
    (filters.ad_source === 'all' || row.source === filters.ad_source) &&
    (filters.ad_type === 'all' || row.ad_type === filters.ad_type) &&
    (filters.placement === 'all' || row.placement === filters.placement);
  const selectedBreakdown = allBreakdown.filter(matchesFilters);

  const focusOnGroup = (row: GroupRow) => {
    setFilters({ ad_source: row.source, ad_type: row.ad_type, placement: row.placement });
  };

  return (
    <div className="str-container">
      <div className="str-header">Sell Through Rate</div>
      {/* Two fields per row: dates, then countries and source, then ad type and placement. */}
      <Row gutter={[16, 16]} className="str-form">
        <Col span={12}>
          <div className="campaign-form-input-container">
            <label>Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
              }}
            />
          </div>
        </Col>
        <Col span={12}>
          <div className="campaign-form-input-container">
            <label>End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
              }}
            />
          </div>
        </Col>
        <Col span={12}>
          <AnghamiAdsFormSelector
            label="Countries *"
            showSearch
            mode="multiple"
            options={availableCountries}
            placeholder="Select one or more countries"
            value={countries}
            onChange={(value: string[]) => {
              setCountries(value);
            }}
          />
        </Col>
        <Col span={12}>
          <AnghamiAdsFormSelector
            label="Source"
            showSearch={false}
            options={sourceFilterOptions}
            value={filters.ad_source}
            onChange={(value: StrFilters['ad_source']) => {
              setFilters({ ...filters, ad_source: value });
            }}
          />
        </Col>
        <Col span={12}>
          <AnghamiAdsFormSelector
            label="Ad Type"
            showSearch={false}
            options={adTypeFilterOptions}
            value={filters.ad_type}
            onChange={(value: StrFilters['ad_type']) => {
              setFilters({ ...filters, ad_type: value });
            }}
          />
        </Col>
        <Col span={12}>
          <AnghamiAdsFormSelector
            label="Placement"
            showSearch={false}
            options={placementFilterOptions}
            value={filters.placement}
            onChange={(value: StrFilters['placement']) => {
              setFilters({ ...filters, placement: value });
            }}
          />
        </Col>
      </Row>
      <div className="str-note">
        Display ads are measured separately and are not included in these numbers.
      </div>
      <button
        type="button"
        className={`calculate-str-btn ${isLoading ? 'loading' : ''} ${
          countries.length === 0 ? 'disabled' : ''
        }`}
        onClick={() => {
          if (isLoading || countries.length === 0) return;
          getSTR();
        }}
      >
        {isLoading ? <SpinningCircle /> : 'Calculate STR'}
      </button>
      {errorMessage && (
        <div className="str-error">
          <strong>Error:</strong> {errorMessage}
        </div>
      )}
      {results && (
        <div className="str-results-container">
          <div className="str-section-title">
            <span>Selected market, {filtersDescription}</span>
            {hasActiveFilters && (
              <button type="button" className="str-reset-filters" onClick={resetFilters}>
                Reset filters
              </button>
            )}
          </div>
          <div className="str-metrics-row">
            <InsightsCard title="Capacity" width="24%" height="auto">
              <div className="str-metric-value">{formatCount(results.total_impressions)}</div>
              <div className="str-metric-hint">impressions served</div>
            </InsightsCard>
            <InsightsCard title="Booked" width="24%" height="auto">
              <div className="str-metric-value">{formatCount(results.reserved_impressions)}</div>
              <div className="str-metric-hint">paid campaigns + GAM</div>
            </InsightsCard>
            <InsightsCard title="In-House" width="24%" height="auto">
              <div className="str-metric-value">{formatCount(results.house_impressions)}</div>
              <div className="str-metric-hint">unsold, filled by Anghami</div>
            </InsightsCard>
            <InsightsCard title="STR" width="24%" height="auto">
              <div className="str-metric-value str-metric-highlight">
                {formatPercent(results.str)}
              </div>
              <div className="str-metric-hint">booked / capacity</div>
            </InsightsCard>
          </div>

          <div className="campaign-form-input-container str-results">
            <label>STR PER DAY FOR THE SELECTED MARKET</label>
            <ResponsiveContainer height={300}>
              <LineChart data={chartData} margin={{ top: 15, right: 10 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" />
                <YAxis yAxisId="impressions" tickFormatter={(value) => formatCount(value)} />
                <YAxis
                  yAxisId="percentage"
                  orientation="right"
                  domain={[0, 100]}
                  tickFormatter={formatPercent}
                />
                <Tooltip
                  formatter={(value: number, name: string) =>
                    name === 'STR' ? formatPercent(value) : formatCount(value)
                  }
                />
                <Legend />
                <Line
                  yAxisId="impressions"
                  type="monotone"
                  dataKey="total_impressions"
                  name="Capacity"
                  stroke={CAPACITY_COLOR}
                  strokeWidth={2}
                />
                <Line
                  yAxisId="impressions"
                  type="monotone"
                  dataKey="reserved_impressions"
                  name="Booked"
                  stroke={BOOKED_COLOR}
                  strokeWidth={2}
                />
                <Line
                  yAxisId="percentage"
                  type="monotone"
                  dataKey="str"
                  name="STR"
                  stroke={STR_COLOR}
                  strokeWidth={2}
                  strokeDasharray="6 3"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="campaign-form-input-container str-results">
            <label>DAILY CAPACITY AND BOOKED IMPRESSIONS</label>
            <Table
              className="str-table"
              size="small"
              pagination={false}
              rowKey="day"
              columns={dailyColumns}
              dataSource={results.str_per_day}
              summary={(rows) => metricsSummaryRow(rows as StrDayRow[], 'Total', 1)}
            />
          </div>

          {allBreakdown.length > 0 && (
            <div className="campaign-form-input-container str-results">
              <label>BREAKDOWN BY SOURCE, AD TYPE AND PLACEMENT</label>
              <div className="str-breakdown-toolbar">
                <div className="str-note">
                  {hasActiveFilters
                    ? 'Highlighted rows match the filters above and add up to the tiles. Click another row to switch.'
                    : 'All groups for the selected market. Click a row to focus the page on that group.'}
                </div>
                {hasActiveFilters && (
                  <button type="button" className="str-reset-filters" onClick={resetFilters}>
                    Reset filters
                  </button>
                )}
              </div>
              <Table
                className={`str-table str-table-clickable ${
                  hasActiveFilters ? 'str-table-filtered' : ''
                }`}
                size="small"
                pagination={false}
                rowKey={(row: GroupRow) => `${row.source}-${row.ad_type}-${row.placement}`}
                rowClassName={(row: GroupRow) =>
                  hasActiveFilters && matchesFilters(row) ? 'str-row-selected' : ''
                }
                columns={breakdownColumns}
                dataSource={allBreakdown}
                onRow={(row: GroupRow) => ({
                  onClick: () => focusOnGroup(row)
                })}
                summary={() => (
                  <>
                    {hasActiveFilters &&
                      metricsSummaryRow(
                        selectedBreakdown,
                        'Selected groups',
                        3,
                        'str-table-total str-table-total-selected'
                      )}
                    {metricsSummaryRow(allBreakdown, 'All groups', 3)}
                  </>
                )}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
